import { useEffect, useState } from "react";
import { Variant } from "../api/schemas";
import { apiVersion } from "../consts/api";
import { ConfirmProps } from "../features/confirm/Confirm";
import { WidthAndSizeFormValues } from "../types/form";

interface UseVariantsArgs {
  colorValue: string;
  widthAndSize: WidthAndSizeFormValues;
  parentCode: string | undefined;
}

export function useVariants({
  colorValue,
  widthAndSize,
  parentCode,
}: UseVariantsArgs) {
  const [variantData, setVariantData] = useState<ConfirmProps["variantData"]>();

  useEffect(() => {
    const leftFootWidth = widthAndSize.leftWidth;
    const leftFootLength = widthAndSize.leftSize;
    const rightFootWidth = widthAndSize.rightWidth;
    const rightFootLength = widthAndSize.rightSize;

    (async () => {
      let variants: VariantsPair;
      if (
        leftFootWidth === rightFootWidth &&
        leftFootLength === rightFootLength
      ) {
        variants = (await getVariantsFullPair({
          parentCode,
          colorValue,
          footWidth: leftFootWidth,
          footLength: leftFootLength,
        })) as VariantsPair;
        if (variants.left.length === 0) {
          throw new Error("variantsの取得に失敗しました");
        }
        setVariantData({
          left: variants.left[0],
          right: variants.left[0],
          heel: variants.left[0].heel.value,
          color: variants.left[0].color,
          totalPrice: variants.left[0].price,
        });
      } else {
        variants = (await getVariantsNotFullPair({
          parentCode,
          colorValue,
          leftFootWidth: leftFootWidth,
          leftFootLength: leftFootLength,
          rightFootWidth: rightFootWidth,
          rightFootLength: rightFootLength,
        })) as VariantsPair;
        if (variants.left.length === 0 || variants.right.length === 0) {
          throw new Error("variantsの取得に失敗しました");
        }
        setVariantData({
          left: variants.left[0],
          right: variants.right[0],
          heel: variants.left[0].heel.value,
          color: variants.left[0].color,
          totalPrice: variants.left[0].price + variants.right[0].price,
        });
      }
    })().catch((error: unknown) => {
      throw error;
    });
  }, [widthAndSize, colorValue, parentCode]);

  return {
    variantData,
  };
}

interface GetVariantsFullPairArgs {
  parentCode: string | undefined;
  colorValue: string;
  footWidth: string;
  footLength: string;
}

interface GetVariantsNotFullPairArgs {
  parentCode: string | undefined;
  colorValue: string;
  leftFootWidth: string;
  leftFootLength: string;
  rightFootWidth: string;
  rightFootLength: string;
}

interface VariantsPair {
  left: Variant[];
  right: Variant[];
}

async function getVariantsFullPair({
  parentCode,
  colorValue,
  footWidth,
  footLength,
}: GetVariantsFullPairArgs) {
  if (parentCode === undefined) throw new Error("parentCodeがundefinedです");

  const setType = "full_pair";
  const response = await fetch(
    `${import.meta.env.VITE_IDEAL_API_FQDN}/${apiVersion}/products/parent_code/${parentCode}/variants?color=${colorValue}&foot_width=${footWidth}&foot_length=${footLength}&set_type=${setType}`,
    {
      method: "GET",
    },
  );
  if (!response.ok) {
    throw new Error(`error: ${response.status}`);
  }
  const text = await response.text();

  let json;
  try {
    json = JSON.parse(text) as unknown;
  } catch {
    json = {};
  }
  return {
    left: json,
    right: json,
  };
}

async function getVariantsNotFullPair({
  parentCode,
  colorValue,
  leftFootWidth,
  leftFootLength,
  rightFootWidth,
  rightFootLength,
}: GetVariantsNotFullPairArgs) {
  if (parentCode === undefined) throw new Error("parentCodeがundefinedです");

  const setTypeLeft = "left_only";
  const responseLeft = await fetch(
    `${import.meta.env.VITE_IDEAL_API_FQDN}/${apiVersion}/products/parent_code/${parentCode}/variants?color=${colorValue}&foot_width=${leftFootWidth}&foot_length=${leftFootLength}&set_type=${setTypeLeft}`,
    {
      method: "GET",
    },
  );

  const setTypeRight = "right_only";
  const responseRight = await fetch(
    `${import.meta.env.VITE_IDEAL_API_FQDN}/${apiVersion}/products/parent_code/${parentCode}/variants?color=${colorValue}&foot_width=${rightFootWidth}&foot_length=${rightFootLength}&set_type=${setTypeRight}`,
    {
      method: "GET",
    },
  );

  if (!responseLeft.ok) {
    throw new Error(`error: ${responseLeft.status}`);
  } else if (!responseRight.ok) {
    throw new Error(`error: ${responseRight.status}`);
  }

  const leftText = await responseLeft.text();
  const rightText = await responseRight.text();

  let leftJson;
  let rightJson;

  try {
    leftJson = JSON.parse(leftText) as unknown;
    rightJson = JSON.parse(rightText) as unknown;
  } catch {
    leftJson = {};
    rightJson = {};
  }
  return {
    left: leftJson,
    right: rightJson,
  };
}
